import {
  forwardRef,
  useEffect,
  useState,
} from "react";

import Typography from "@mui/material/Typography";

import {
  Box,
  CircularProgress,
} from "@mui/material";

import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";

import {
  fetchFromApi,
} from "../helpers/fetchFromApi";

import {
  AppBar,
} from "../styled-components/AppBar";

import {
  extractFlag,
} from "./Help";

import {
  LeagueTablePanel,
} from "./LeagueTablePanel";


const Transition = forwardRef(
  function Transition(
    props,
    ref
  ) {
    return (
      <Slide
        direction="up"
        ref={ref}
        {...props}
      />
    );
  }
);


export function LeagueStandingsDialog({
  league,
  setOpen,
}) {
  const [
    standings,
    setStandings,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);


  function handleClose() {
    setOpen(false);
  }


  useEffect(() => {
    if (!league) {
      setStandings([]);
      return;
    }


    async function fetchTeams(
      seasonId
    ) {
      const response =
        await (
          await fetchFromApi(
            `fetch-data-v3/teams/seasons/${seasonId}`
          )
        ).json();

      return response.data || [];
    }


    function getDetailValue(
      team,
      typeId
    ) {
      const value =
        team.details?.find(
          (detail) =>
            detail.type_id ===
            typeId
        )?.value;

      return Number(
        value ?? 0
      );
    }


    async function fetchSeasonTeams() {
      setLoading(true);
      setStandings([]);

      try {
        const seasonResponse =
          await fetchFromApi(
            "fetch-data-v3/leagues/" +
              league.id +
              "?per_page=50&include=seasons"
          );

        const season =
          await seasonResponse.json();

        const currentSeason =
          season?.data?.seasons?.find(
            (seasonItem) =>
              seasonItem.is_current ===
              true
          );

        if (!currentSeason) {
          return;
        }

        const currentSeasonId =
          currentSeason.id;


        const [
          teamData,
          standingsResponse,
        ] = await Promise.all([
          fetchTeams(
            currentSeasonId
          ),

          fetchFromApi(
            `fetch-data-v3/standings/seasons/${currentSeasonId}?include=details;participant`
          ).then(
            (response) =>
              response.json()
          ),
        ]);


        const newRows = [];

        for (
          const team of
          standingsResponse?.data ||
          []
        ) {
          const participant =
            team.participant ||
            teamData.find(
              (teamTwo) =>
                teamTwo.id ===
                team.participant_id
            );


          const overall = {
            games_played:
              getDetailValue(
                team,
                129
              ),

            won:
              getDetailValue(
                team,
                130
              ),

            draw:
              getDetailValue(
                team,
                131
              ),

            lost:
              getDetailValue(
                team,
                132
              ),

            goals_scored:
              getDetailValue(
                team,
                133
              ),

            goals_against:
              getDetailValue(
                team,
                134
              ),

            points:
              getDetailValue(
                team,
                187
              ),
          };


          const home = {
            games_played:
              getDetailValue(
                team,
                135
              ),

            won:
              getDetailValue(
                team,
                136
              ),

            draw:
              getDetailValue(
                team,
                137
              ),

            lost:
              getDetailValue(
                team,
                138
              ),

            goals_scored:
              getDetailValue(
                team,
                139
              ),

            goals_against:
              getDetailValue(
                team,
                140
              ),

            points:
              getDetailValue(
                team,
                185
              ),
          };


          const away = {
            games_played:
              getDetailValue(
                team,
                141
              ),

            won:
              getDetailValue(
                team,
                142
              ),

            draw:
              getDetailValue(
                team,
                143
              ),

            lost:
              getDetailValue(
                team,
                144
              ),

            goals_scored:
              getDetailValue(
                team,
                145
              ),

            goals_against:
              getDetailValue(
                team,
                146
              ),

            points:
              getDetailValue(
                team,
                186
              ),
          };


          newRows.push({
            id:
              team.id ??
              team.participant_id,

            team_id:
              team.participant_id,

            team_name:
              participant?.name ||
              team.participant?.name ||
              String(
                team.participant_id
              ),

            logo_path:
              participant?.image_path ||
              participant?.logo_path ||
              "",

            overall,
            home,
            away,
          });
        }

        setStandings(
          newRows
        );
      } catch (error) {
        console.error(
          "[LeagueStandingsDialog]",
          error
        );
      } finally {
        setLoading(false);
      }
    }


    fetchSeasonTeams();

  }, [league]);


  return (
    <Dialog
      fullScreen
      open={Boolean(league)}
      onClose={handleClose}
      TransitionComponent={
        Transition
      }
      PaperProps={{
        sx: {
          background:
            "linear-gradient(145deg, #eaf4f8 0%, #f4f9fb 32%, #e5f2f7 68%, #f7fbfc 100%)",

          backgroundAttachment:
            "fixed",
        },
      }}
    >
      <AppBar
        sx={{
          position: "relative",
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={
              handleClose
            }
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Typography
            sx={{
              ml: 2,
              flex: 1,
            }}
            variant="h6"
            component="div"
          >
            <Box
              sx={{
                display: "flex",
                alignItems:
                  "center",
                gap: "0.5em",
              }}
            >
              <img
                style={{
                  width: "1em",
                }}
                alt={
                  league?.country
                    ?.data?.name
                }
                title={
                  league?.country
                    ?.data?.name
                }
                src={`data:image/svg+xml;utf8,${encodeURIComponent(
                  extractFlag(
                    league?.country
                      ?.data?.extra
                  )
                )}`}
              />

              {league?.name}
              {" "}
              Standings
            </Box>
          </Typography>
        </Toolbar>
      </AppBar>


      <Box
        sx={{
          width: "100%",
          maxWidth: 1220,
          mx: "auto",

          px: {
            xs: 1.25,
            sm: 2,
          },

          py: 2.25,
        }}
      >
        {loading ? (
          <Box
            sx={{
              minHeight: 260,

              display: "flex",

              alignItems:
                "center",

              justifyContent:
                "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : standings.length ? (
          <LeagueTablePanel
            rows={standings}
            showTableMode
            renderStats
          />
        ) : (
          <Box
            sx={{
              minHeight: 180,

              display: "flex",

              alignItems:
                "center",

              justifyContent:
                "center",
            }}
          >
            <Typography
              color="text.secondary"
            >
              Keine Tabelle verfügbar.
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
